/****************************************************
 * Module Name:  commissionPayment.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useEffect } from "react";
import Constants from "../../utility/constants";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import CommissionProxy from "../../utility/proxy/manager/commissionProxy";
import { RefreshOutlined, SearchOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import Utility from "../../utility/utility";
import { BsFilePdf } from "react-icons/bs";
import PaymentModal from "./paymentModal";
import CommonSnackbar from "../common/commonSnackBar";
import UserProxy from "../../utility/proxy/manager/userProxy";
import NoPermission from "../common/noPermission";
import Select from "react-select";
import PageTitle from "../common/pageTitle";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";

const CommissionPayment = () => {
  const [data, setData] = useState([]);
  const [broker, setBroker] = useState([]);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [fetchData, setFetchData] = useState(false);
  const [listLoader, setListLoader] = useState(false);
  const [errorSnackbar, seterrorSnackbar] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingAmount, setPendingAmount] = useState(0.00);
  const [filterValues, setFilterValues] = useState({});
  const [paymentLoader, setPaymentLoader] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  useEffect(() => {
    PageTitle("REPORT_COMMISSION_PAYMENT");
    checkPermission();
  }, []);

  const checkPermission = () => {
    let allowed = Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.REPORT_COMMISSION_PAYMENT
    ) !== undefined
    if (allowed) {
      fetchBrokers();
    } else {
      return <NoPermission />
    }
  }

  useEffect(() => {
    if (fetchData) {
      if (broker) {
        handleSearch();
      }
    }
  }, [fetchData, broker]);

  const fetchBrokers = () => {
    UserProxy.GetAllBrokerMini((response) => {
      if (response)
        processBrokerData(response);
    });
  };

  const processBrokerData = (response) => {
    let optionsArray = [];
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.companyname,
      };
      optionsArray.push(option);
      return true;
    });
    setBrokerOptions(optionsArray);
    setBroker([optionsArray[0]]);
    setFetchData(true);
  };


  const handleSearch = () => {
    let data = [broker.value];
    setListLoader(true);
    CommissionProxy.GetAllCommission(data, (responseData) => {
      if (responseData) {

        let sumPendingAmount = 0;
        let tdata = responseData.map((data) => {
          let obj = {
            ...data,
            pendingamount: Number(data.amount) - Number(data.paidamount),
          };
          sumPendingAmount += obj.pendingamount
          return obj;
        }).sort((a, b) => a.pendingamount - b.pendingamount);

        const uniqueFilterValues = {
          type: Utility.getDistinctOptions(responseData, "type"),
          status: Utility.getDistinctOptions(responseData, "status"),
        };
        setFilterValues(uniqueFilterValues);
        setData(tdata);
        setPendingAmount(Utility.ConvertNumberWithSpecifiedDigits(sumPendingAmount));
        setListLoader(false);
        setFetchData(false);
      } else {
        setPendingAmount(0.0);
        seterrorSnackbar(true)
        setListLoader(false);
        setFetchData(false);
      }
    });
  };

  const handleValidation = (data) => {
    //TODO do validation if needed here
    return true;
  };

  const handlePayment = (data) => {
    setPaymentLoader(true);
    CommissionProxy.UpdateCommissionPayment(data, (response) => {
      if (response) {
        setShowPaymentModal(false);
        handleSearch();
        setPaymentLoader(false);
      }
    });
  };

  const initialColumns = () => [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "userid",
      hheaderName: "USER ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "brokercompanyname",
      hheaderName: "BROKER",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "date",
      headerName: "DATE",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "updatedat",
      headerName: "UPDATED AT",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "invoiceid",
      headerName: "INVOICE ID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      minWidth: 50,
      flex: 0.5,
    },
    {
      field: "type",
      headerName: "TYPE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.type : [],
      filterOperators: Utility.combinedFilters(),
      minWidth: 120,
      flex: 1,
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      flex: 1,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "paidamount",
      headerName: "PAID AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 1,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "pendingamount",
      headerName: "PENDING AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      flex: 1,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "invoiceamount",
      headerName: "INVOICE AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 1,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "finalamount",
      headerName: "FINAL AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 1,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "status",
      headerName: "STATUS",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.status : [],
      filterOperators: Utility.combinedFilters(),
      width: 100,
    },
    {
      field: "pdf",
      headerName: "DOWNLOAD PDF",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      renderCell: (params) => (
        <a
          href={`data:application/pdf;base64,${params.value}`}
          download="Commission.pdf"
          style={{ color: "red" }}
        >
          <BsFilePdf size={25} />
        </a>
      ),
    },
  ];

  const rows = data;
  const columns = initialColumns();

  return (
    <Box>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => seterrorSnackbar(false)}
      />
      {showUserDetailModal ? (
        <UserDetailModal
          show={true}
          handleClose={() => setShowUserDetailModal(false)}
          handleSelect={(selectedOption) => {
            const selectedCustomers = brokerOptions.filter(opt =>
              selectedOption.includes(opt.value)
            );
            setBroker([selectedCustomers[0]]);
            setShowUserDetailModal(false)
          }}
          userids={brokerOptions.filter(option => option.value !== "*").map(cust => cust.value)}
          selectedIds={broker.length !== 0 ? broker.map(iCustomer => iCustomer.value) : []}
          usertype={"U"}
          loading={showUserDetailModal}
          isMulti={false}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          my: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: Constants.loggedInUser.usertype !== "B" ? "25%" : "30%",
            gap: "20px",
            pl: 1
          }}
        >
          <Box sx={{ alignSelf: "center", mt: 2, width: "185px" }}>
            <Typography>Broker Company-</Typography>
          </Box>
          {Constants.loggedInUser.usertype !== "B" ? (
            <Box
              sx={{
                alignSelf: "center",
                width: "90%",
                mt: 0,
                maxWidth: "90%",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Select menuPlacement="auto"
                  blurInputOnSelect={false}
                  className="basic-single"
                  classNamePrefix="select"
                  value={broker}
                  isSearchable={true}
                  name="broker"
                  menuPortalTarget={document.body}
                  styles={Constants.Styles}
                  options={brokerOptions}
                  onChange={(selectedOption) => setBroker([selectedOption])}
                />
              </Box>
              <Tooltip title="View More Data">
                <span style={{ cursor: "pointer", marginLeft: "15px", marginTop: "0px" }}>
                  <OpenInNewIcon
                    fontSize="medium"
                    color="action"
                    onClick={() => setShowUserDetailModal(true)}
                  />
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Typography mt={2} fontWeight={600}>
              {Constants.loggedInUser.username}
            </Typography>
          )}
        </Box>
        <Box sx={{ marginTop: "17px", ml: 2 }}>
          <LoadingButton
            variant="contained"
            onClick={() => handleSearch()}
            startIcon={<SearchOutlined size="large" />}
            loading={listLoader}
            loadingPosition="start"
            disabled={
              brokerOptions.length === 0 &&
              Constants.loggedInUser.usertype !== "B"
            }
          >
            Search
          </LoadingButton>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          width: "100%",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
            color: "#1976d2",
            padding: "4px 5px",
            borderRadius: "4px",
          },
          "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
            color: "#1976d2",
            padding: "5px 5px",
            borderRadius: "4px",
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          height: "80vh",
        }}
      >
        <DataGridPro
          disableRowSelectionOnClick={true}
          slots={{ toolbar: GridToolbar }}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              color: '#fff',
              backgroundColor: 'grey',
            },
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "4px",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "12px",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "18px",
            },

            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold'
          }}
          density="compact"
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={listLoader}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns id, the other columns will remain visible
                locationid: false,
                userid: false,
              },
            },
            pagination: {
              paginationModel: Constants.pageSize,
            },
          }}
          pageSizeOptions={Constants.pageSizeOptions}
          pagination
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
              },
            },
          }}
        />
        <Box
          style={{
            position: "absolute",
            top: 5,
            right: "270px",
          }}
        >
          {listLoader ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlined
                onClick={() => handleSearch()}
                color="primary"
                fontSize="medium"
                sx={{ cursor: "pointer", mr: 1 }}
              />
            </Tooltip>
          )}
          <Tooltip title="Payment">
            <Button
              variant="outlined"
              component="label"
              size="small"
              color="success"
              sx={{ ml: "5px" }}
              onClick={() => setShowPaymentModal(true)}
            >
              Pay: ${pendingAmount}
            </Button>
          </Tooltip>
          {showPaymentModal && (
            <PaymentModal
              show={showPaymentModal}
              setShow={setShowPaymentModal}
              handleValidation={handleValidation}
              handleSave={handlePayment}
              sourceData={data}
              loading={paymentLoader}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CommissionPayment;
